// About / Bio section for the press kit.
// Renders the short bio as primary copy and reveals the long bio via an
// in-place expander. Decision: inline expander over a /press/bio sub-route
// — keeps press on the page and avoids the browsing tax of a detail view.
//
// The one-liner (still exported from data/pressBio.js for the kit zip)
// is intentionally NOT rendered here: it's near-identical to the short
// bio's opening sentence, so stacking them just makes press read the
// same definition twice in a row.
//
// Layout: portrait photo (Dan + Wella at Courtesy Diner) on the right at
// desktop, stacks above the copy on mobile. The photo isn't decorative —
// it does the job of grounding the bio in a real human pairing, which is
// part of why press kits matter.

import { useState } from "react";
import { SHORT_BIO, LONG_BIO } from "../data/pressBio";
import { findSlot, PRESS_PHOTOS } from "../data/pressPhotos";
import PanBar from "../components/PanBar";

export default function BioSection() {
  const [expanded, setExpanded] = useState(false);
  const portrait = findSlot("bio-portrait") || PRESS_PHOTOS.find((p) => p.category === "headshot");

  return (
    <section
      id="about"
      style={{
        background: "#0f2a47",
        padding: "5rem 1.25rem",
        borderTop: "1px solid rgba(252,179,31,0.12)",
        borderBottom: "1px solid rgba(252,179,31,0.12)",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "2.6rem" }}>
          <PanBar width={72} height={3} margin="0 auto 1.1rem" />
          <h2 style={{
            margin: 0,
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(1.8rem, 4.5vw, 2.6rem)",
            color: "var(--jop-gold)",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}>
            About
          </h2>
        </div>

        <div className="jop-bio-grid">
          {portrait && (
            <figure style={{ margin: 0 }}>
              <img
                src={`/${portrait.variants[1200] || portrait.variants[600]}`}
                srcSet={`/${portrait.variants[600]} 600w, /${portrait.variants[1200]} 1200w, /${portrait.variants[2400]} 2400w`}
                sizes="(max-width: 760px) 100vw, 460px"
                alt={portrait.alt}
                loading="lazy"
                decoding="async"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  borderRadius: "14px",
                  border: "1px solid rgba(252,179,31,0.18)",
                  boxShadow: "0 12px 36px rgba(0,0,0,0.45), 0 0 0 1px rgba(252,179,31,0.08)",
                }}
              />
              <figcaption style={{
                marginTop: "0.7rem",
                fontFamily: "var(--jop-display)",
                fontSize: "0.78rem",
                color: "rgba(255,218,173,0.55)",
                letterSpacing: "0.04em",
                textAlign: "center",
              }}>
                Dan Drake &amp; Wella Kelly — Courtesy Diner, St. Louis
              </figcaption>
            </figure>
          )}

          <div style={{ fontFamily: "var(--jop-display)", color: "var(--jop-cream)" }}>
            {SHORT_BIO.map((p, i) => (
              <p key={i} style={{
                margin: i === 0 ? "0 0 1.1rem" : "0 0 1.1rem",
                fontSize: "1.02rem",
                lineHeight: 1.7,
                opacity: 0.95,
              }}>
                {p}
              </p>
            ))}

            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls="long-bio"
              style={{
                background: "transparent",
                color: "var(--jop-gold)",
                border: "1px solid rgba(252,179,31,0.4)",
                borderRadius: "999px",
                padding: "0.55rem 1.1rem",
                fontFamily: "var(--jop-display)",
                fontWeight: 700,
                fontSize: "0.88rem",
                letterSpacing: "0.04em",
                cursor: "pointer",
                marginTop: "0.4rem",
                transition: "background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(252,179,31,0.1)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {expanded ? "Hide the long version ↑" : "Read the full bio ↓"}
            </button>

            {expanded && (
              <div
                id="long-bio"
                style={{
                  marginTop: "1.4rem",
                  paddingTop: "1.4rem",
                  borderTop: "1px solid rgba(252,179,31,0.15)",
                }}
              >
                {LONG_BIO.map((p, i) => (
                  <p key={i} style={{
                    margin: "0 0 1.1rem",
                    fontSize: "1rem",
                    lineHeight: 1.75,
                    color: "var(--jop-cream)",
                    opacity: 0.92,
                  }}>
                    {p}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .jop-bio-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
          gap: 2.4rem;
          align-items: start;
        }
        @media (max-width: 800px) {
          .jop-bio-grid {
            grid-template-columns: 1fr;
            gap: 1.6rem;
          }
        }
      `}</style>
    </section>
  );
}
